import { getRenderingRef, forceUpdate, getElement } from '@stencil/core';
import { createBrowserHistory } from './createBrowserHistory.mjs';
import { logger } from './logger.mjs';
import { matchPath } from './match-path.mjs';
import { compile } from './path-to-regex.mjs';

var __rest = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
class InternalRouter {
    constructor({ root = '/', titleSuffix = '', document: passedDoc, } = {}) {
        var _a;
        this.root = '/';
        this.updateLocation = (location) => {
            // Remove the root URL if found at beginning of string
            const pathname = location.pathname.indexOf(this.root) === 0
                ? '/' + location.pathname.slice(this.root.length)
                : location.pathname;
            location = Object.assign(Object.assign({}, location), { pathname });
            this.location = location;
            this.matchCache.clear();
        };
        this.updateScroll = ({ scrollPosition }) => {
            if (!scrollPosition)
                return;
            window.scrollTo(...scrollPosition);
        };
        this.refUtils = new Map([
            [
                getRenderingRef,
                { forceUpdate: forceUpdate, getElement: getElement },
            ],
        ]);
        this.with = (getRenderingRef, forceUpdate, getElement) => {
            if (!this.refUtils.has(getRenderingRef)) {
                this.refUtils.set(getRenderingRef, {
                    forceUpdate,
                    getElement,
                });
            }
            return this;
        };
        this.interestedParties = new Set();
        this.updateInterestedParties = () => {
            for (const [getRenderingRef, { forceUpdate }] of this.refUtils) {
                const ref = getRenderingRef();
                if (ref) {
                    this.interestedParties.add(() => forceUpdate(ref));
                }
            }
        };
        this.informInterestedParties = () => {
            const interestedParties = Array.from(this.interestedParties);
            this.interestedParties.clear();
            for (const update of interestedParties) {
                update();
            }
        };
        this.matchCache = new Map();
        this.setDocumentTitle = (title, noSuffix, document = this.history.win.document) => {
            document.title = `${title}${noSuffix ? '' : this.titleSuffix}`;
        };
        this.push = (path, ...args) => this.history.push(this.getUrl(path), ...args);
        this.replace = (path, ...args) => this.history.replace(this.getUrl(path), ...args);
        this.goBack = (...args) => this.history.goBack(...args);
        this.goForward = (...args) => this.history.goForward(...args);
        /** Load a specific page from the session history. You can use it to move forwards and backwards through the history depending on the value of a parameter. */
        this.go = (...args) => this.history.go(...args);
        this.pendingActions = new Map();
        this.getActionElement = () => {
            for (const [getRenderingRef, { getElement }] of this.refUtils) {
                const ref = getRenderingRef();
                if (ref) {
                    return getElement(ref);
                }
            }
        };
        this.queueActionOnElement = async (element) => {
            var _a, _b;
            await ((_a = element === null || element === void 0 ? void 0 : element.componentOnReady) === null || _a === void 0 ? void 0 : _a.call(element));
            if (element === null || element === void 0 ? void 0 : element.isConnected) {
                (_b = this.pendingActions.get(element)) === null || _b === void 0 ? void 0 : _b.forEach((cb) => cb());
            }
            this.pendingActions.delete(element);
        };
        this.fillPath = (path, parameters) => {
            var _a, _b;
            const params = (_b = parameters !== null && parameters !== void 0 ? parameters : (_a = this.match({
                path,
                exact: false,
                strict: true,
            })) === null || _a === void 0 ? void 0 : _a.params) !== null && _b !== void 0 ? _b : {};
            return compile(path)(params);
        };
        const doc = passedDoc ||
            (getRenderingRef() &&
                ((_a = getElement(getRenderingRef())) === null || _a === void 0 ? void 0 : _a.ownerDocument)) ||
            document;
        this.root = root;
        this.titleSuffix = titleSuffix;
        this.history = createBrowserHistory(doc.defaultView);
        this.history.listen(this.updateLocation);
        this.history.listen(this.informInterestedParties);
        this.history.listen(this.updateScroll);
        this.updateLocation(this.history.location);
    }
    get match() {
        this.updateInterestedParties();
        return (options) => {
            if (typeof options.path === 'string' &&
                options.path.startsWith('#')) {
                return options.path === this.location.hash
                    ? {
                        path: options.path,
                        url: this.location.pathname,
                        isExact: !!options.exact,
                        params: {},
                    }
                    : null;
            }
            const key = JSON.stringify(options);
            if (this.matchCache.has(key)) {
                return this.matchCache.get(key);
            }
            const match = matchPath(this.location.pathname, options);
            this.matchCache.set(key, match);
            return match;
        };
    }
    getUrl(url) {
        if (typeof url === 'string') {
            if (url.charAt(0) === '#') {
                return url;
            }
            const root = this.root;
            // Don't allow double slashes
            if (url.charAt(0) === '/' && root.charAt(root.length - 1) === '/') {
                return root.slice(0, root.length - 1) + url;
            }
            return root + url;
        }
        const { pathname } = url, rest = __rest(url, ["pathname"]);
        return Object.assign({ pathname: this.getUrl(pathname) }, rest);
    }
    get action() {
        this.updateInterestedParties();
        return (callback) => {
            const el = this.getActionElement();
            if (!el) {
                callback();
                return () => {
                    logger.warn('Attempted to cancel an action that was already called');
                };
            }
            if (this.pendingActions.has(el)) {
                this.pendingActions.get(el).add(callback);
            }
            else {
                this.pendingActions.set(el, new Set([callback]));
                this.queueActionOnElement(el);
            }
            return () => {
                this.pendingActions.get(el).delete(callback);
            };
        };
    }
}

export { InternalRouter };
